import 'swiper/css'

import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type { PlaylistCurated } from 'types/playlist'

import Carousel from 'components/common/carousel'
import {
  CarouselSkeleton,
  CuratedPlaylistCardSkeleton,
} from 'components/common/carousel/carousel-skeleton'
import { Box } from 'components/ds'

import CuratedPlaylistCard from '../../curated-playlists'

type Props = {
  curatedPlaylists: (PlaylistCurated | null | undefined)[]
  loading?: boolean
}

const CuratedPlaylistsSection = ({
  curatedPlaylists,
  loading = false,
}: Props): React.ReactNode => {
  const navigate = useNavigate()
  const [isHovered, setIsHovered] = useState<number | null>(null)

  const validPlaylists = useMemo(
    () => curatedPlaylists.filter(Boolean) as PlaylistCurated[],
    [curatedPlaylists],
  )

  const handleVisitPlaylist = useCallback(
    (playlistIndex: number, playlist: PlaylistCurated) => {
      sendDiscoverMixpanelEvent('Click Image', {
        context: 'Discover Music Home',
        playlistId: playlist.id,
        playlist: playlist.name,
        playlistBusiness: playlist?.business?.business_name,
        playlistBusinessId: playlist?.business?.business_id,
      })

      // Pass playlist data via navigation state to avoid Redux lookups (too slow)
      navigate(`/discover/playlist?index=${playlistIndex}`, {
        state: { playlist },
      })
    },
    [navigate],
  )

  // Show skeleton while loading
  if (loading) {
    return (
      <CarouselSkeleton
        itemCount={3}
        ItemSkeleton={CuratedPlaylistCardSkeleton}
      />
    )
  }

  if (validPlaylists.length === 0) {
    return null
  }

  return (
    <Box mb="7">
      <Carousel
        navKey="curated-playlists"
        slidesPerView="auto"
        spaceBetween={16}
        useContainerBreakpoints
      >
        {validPlaylists.map((playlist, index) => (
          <CuratedPlaylistCard
            key={playlist.id || index}
            playlist={playlist}
            index={index}
            onHover={setIsHovered}
            isHovered={isHovered}
            onClick={() => handleVisitPlaylist(index, playlist)}
          />
        ))}
      </Carousel>
    </Box>
  )
}

export default CuratedPlaylistsSection
